<script lang="ts">
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { cn } from '@/lib/utils';
  import SendToWorkoutPlayerButton from './SendToWorkoutPlayerButton.svelte';
  import { deleteCalendarEvent, hideActivity, starActivity, switchKeptDuplicate, unlinkDuplicate } from '@/Util/api';
  import SendToStravaButton from './SendToStravaButton.svelte';
  import ShareActivityButton from './ShareActivityButton.svelte';
  import { Button } from '@/lib/components/ui/button';
  import ExerciseDialog from './ExerciseDialog.svelte';
  import ConfirmationPopover from './ConfirmationPopover.svelte';
  import TodayActivityCardMatchingButton from './TodayActivityCardMatchingButton.svelte';
  import CommentsButton from '@/Comments/CommentsButton.svelte';
  import type { TodaysActivities } from '@/Common/constants';
  import { today } from '@/time';
  import { getContext } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import { FORECAST_TOOLS, type ForecastTools } from './forecastTools';
  import { startOfDay } from 'date-fns';
  import DayAvailabilityPopover from '../Calendar/DayAvailabilityPopover.svelte';

  export let activity: Exercise;
  export let todaysActivities: TodaysActivities;
  export let disabled: boolean = false; // This controls whether the buttons on the card are disabled
  export let transparent: boolean = false; // This controls whether the buttons on the card are shown with transparent styling

  const myFitness = getContext<MyFitnessStores>('myFitness');
  const { commentCounts } = myFitness;

  // Set by the Today page: the Availability button and the check of whether Adapt Forecast is needed after a change.
  const forecastTools = getContext<ForecastTools | undefined>(FORECAST_TOOLS);

  //@ts-ignore
  const Xert = window.Xert;

  let detailsDialogOpen: boolean = false;
  let rootDiv: HTMLElement;

  $: isForecast = Activity.isForecast(activity)
  $: isForecastEvent = Activity.isForecastEvent(activity)
  $: isPlanned = Activity.isPlanned(activity)
  $: isStarred = Activity.isStarred(activity)
  $: isHidden = Activity.isHidden(activity)
  $: isPlaceholder = Activity.isPlaceholder(activity)
  $: isPinned = Activity.isPinned(activity)
  $: isGoalEvent = Activity.isGoalEvent(activity)

  // @ts-ignore
  const jq = window.jQuery;

  function starActivityWrapper() {
    todaysActivities.update((prev) => {
      return prev.map((a) => {
        if (a.path === activity.path) {
          return { ...a, favorite: !isStarred };
        }
        return a;
      });
    });
    starActivity(activity.path, isStarred);
  }

  function disableActivityWrapper() {
    hideActivity(activity.path, isHidden);
    todaysActivities.update((prev) => {
      return prev.map((a) => {
        if (a.path === activity.path) {
          return { ...a, enabled: isHidden, matched_activity: isHidden ? a.matched_activity : null };
        }
        if (Activity.isMatchedAtoB(activity, a)) {
          return { ...a, num_matches: Math.max(0, Math.max(0, a.num_matches) + (isHidden ? 1 : -1)) };
        }
        return a;
      });
    });
  }

  function deleteActivity(path: string) {
    deleteCalendarEvent(path).finally(() => forecastTools?.refreshAdaptIndicator());
    if (!isForecast) {
      todaysActivities.update((prev) => {
        return prev.filter((a) => a.path !== path).map((a) => {
          if (Activity.isMatchedAtoB(activity, a)) {
            return { ...a, num_matches: Math.max(0, a.num_matches - 1) };
          }
          return a;
        });
      });
    }
  }

  async function unlinkDuplicateWrapper(path: string) {
    disabled = true;
    await unlinkDuplicate(path);
    todaysActivities.update((prev) => {
      return prev.map((a) => {
        if (a.path === path) {
          return { ...a, duplicate_group_id: null, is_currently_selected_duplicate: false };
        }
        return a;
      });
    });
    disabled = false;
  }

  async function switchKeptDuplicateWrapper(path: string) {
    disabled = true;
    await switchKeptDuplicate(path);
    jq('[data-toggle="tooltip"]').tooltip('hide');
    if (activity?.duplicate_group_id !== undefined) {
      const matched_activity = todaysActivities.activities.find((a) => a.duplicate_group_id === activity.duplicate_group_id && a.is_currently_selected_duplicate)?.matched_activity ?? null;
      todaysActivities.update((prev) => {
        return prev.map((a) => {
          if (a.duplicate_group_id === activity.duplicate_group_id) {
            return { ...a, is_currently_selected_duplicate: a.path === path, enabled: a.path === path, matched_activity: a.path === path ? matched_activity : null };
          }
          return a;
        });
      });
    }
    disabled = false;
  }

  function pinActivity() {
    Promise.resolve(Activity.togglePin(activity)).finally(() => forecastTools?.refreshAdaptIndicator());
    todaysActivities.update((prev) => {
      return prev.map((a) => {
        if (a.path === activity.path) {
          return { ...a, pinned: !isStarred };
        }
        return a;
      });
    });
  }

</script>

<div bind:this={rootDiv} class="flex flex-row flex-wrap gap-2 items-center">
  {#if isForecast && new Date(activity.start_date) > $today && !isGoalEvent}
    <div class="flex flex-row flex-wrap gap-2 items-center">
      <Button variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} on:click={(e) => {
        e.stopPropagation();
        e.currentTarget.dispatchEvent(new CustomEvent('editEvent', {
          bubbles: true,
          detail: { date: new Date(activity.start_date), exercise: activity }
        }));
      }}>
        Choose Training
      </Button>
    </div>
  {/if}
  {#if forecastTools && isForecast && !isGoalEvent && new Date(activity.start_date) >= startOfDay($today)}
    <!-- Saving it changes the calendar settings, which checks Adapt Forecast again on its own -->
    <DayAvailabilityPopover date={new Date(activity.start_date)} asModal let:builder>
      <Button builders={[builder]} variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} on:click={(e) => e.stopPropagation()}>
        <i class='fa-light fa-clock text-xs mr-1' /> Availability
      </Button>
    </DayAvailabilityPopover>
  {/if}

  <SendToStravaButton {todaysActivities} {activity} {disabled} className={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} />
  <ShareActivityButton {activity} {disabled} className={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} />
  <!-- A completed activity detected as a workout has already performed it, so there is nothing left to send. -->
  {#if activity.workout_id && isPlanned}
    <SendToWorkoutPlayerButton {disabled} workout={{ ...activity, path: activity.workout_id }} />
  {/if}
  {#if !isPlaceholder}
    <div class='flex flex-row flex-wrap gap-2 items-center'>
      {#if Activity.isActivity(activity) && activity.path && Xert._hasMessagingFeature}
        <CommentsButton
          activityPath={activity.path}
          activityName={activity.name}
          labelled
          total={$commentCounts[activity.path]?.total}
          unread={$commentCounts[activity.path]?.unread}
        />
      {/if}
      {#if !isPlanned}
        <Button variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} on:click={(e) => { e.stopPropagation(); starActivityWrapper(); }} >
          <i class={`${isStarred ? 'fa-solid' : 'fa-light'} fa-star text-xs mr-1`} /> {isStarred ? 'Unfavourite' : 'Favourite'}
        </Button>
      {/if}
      <ConfirmationPopover title="{isForecastEvent ? 'Clear' : 'Delete'} this activity?" onConfirm={() => deleteActivity(activity.path)} let:builder>
        <Button variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} builders={[builder]} on:click={(e) => e.stopPropagation()} >
          <i class='fa-light {isForecastEvent ? 'fa-xmark-large' : 'fa-trash'} text-xs mr-1' /> {isForecastEvent ? 'Clear' : 'Delete'}
        </Button>
      </ConfirmationPopover>
      {#if isForecastEvent && !isPlaceholder}
        <Button variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} on:click={pinActivity} >
          <i class='{isPinned ? 'fa-solid' : 'fa-light'} fa-thumbtack text-xs mr-1' /> {isPinned ? 'Unpin' : 'Pin'}
        </Button>
      {/if}
      {#if !isPlanned}
        {#if Activity.isDuplicate(activity)}
          <Button variant='outline' size='sm' on:click={(e) => { e.stopPropagation(); unlinkDuplicateWrapper(activity.path); }} >
            <i class='fa-light fa-link-slash text-xs mr-1' /> Unlink Duplicate
          </Button>
          <Button variant='outline' size='sm' on:click={() => switchKeptDuplicateWrapper(activity.path)}>
            <i class='fa-light fa-swap text-xs mr-1' /> Swap Chosen Duplicate
          </Button>
        {:else}
          <Button variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} on:click={(e) => { e.stopPropagation(); disableActivityWrapper(); }} >
            <i class={`${isHidden ? 'fa-solid' : 'fa-light'} fa-eye-slash text-xs mr-1`} /> {isHidden ? 'Unhide' : 'Hide'}
          </Button>
        {/if}
      {/if}
    </div>
  {/if}

  {#if !isHidden}
    <TodayActivityCardMatchingButton {todaysActivities} {activity} {disabled} {transparent} />
  {/if}

  {#if !isPlaceholder}
    <Button variant='outline' size='sm' class={cn(isHidden ? 'bg-transparent text-muted-foreground' : '')} on:click={(e) => { e.stopPropagation(); detailsDialogOpen = true; }}>
      More Details
    </Button>
  {/if}
</div>

<ExerciseDialog bind:open={detailsDialogOpen} exercise={activity} hideScheduleButtons {todaysActivities} {rootDiv} />
